import { useCallback, useEffect, useState } from 'react';

type ToastStatus = 'success' | 'error' | 'info';

type ToastData = {
  id: number;
  title: string;
  description?: string;
  status: ToastStatus;
};

let toastId = 0;

const toastListeners: Set<(toast: ToastData) => void> = new Set();
const dismissListeners: Set<(id: number) => void> = new Set();

export function toast({
  title,
  description,
  status = 'info',
}: {
  title: string;
  description?: string;
  status?: ToastStatus;
  duration?: number | null;
  isClosable?: boolean;
}) {
  const id = ++toastId;
  const data: ToastData = { id, title, description, status };
  toastListeners.forEach((fn) => fn(data));
  setTimeout(() => {
    dismissListeners.forEach((fn) => fn(id));
  }, 5000);
  return id;
}

export function useToast() {
  return { toast };
}

const statusStyles: Record<ToastStatus, string> = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  info: 'bg-gray-800 text-white',
};

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((t: ToastData) => {
    setToasts((prev) => [...prev, t]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    toastListeners.add(addToast);
    dismissListeners.add(removeToast);
    return () => {
      toastListeners.delete(addToast);
      dismissListeners.delete(removeToast);
    };
  }, [addToast, removeToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed right-4 bottom-4 z-[9999] flex max-w-sm flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`rounded-md px-4 py-3 shadow-lg ${statusStyles[t.status]}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">{t.title}</p>
              {t.description && (
                <p className="mt-0.5 text-xs opacity-90">{t.description}</p>
              )}
            </div>
            <button
              onClick={() => removeToast(t.id)}
              aria-label="Schließen"
              className="ml-2 text-lg leading-none opacity-70 hover:opacity-100"
            >
              &times;
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
